import { useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { useBaseConfig } from '@zurich/common/contexts';
import { useTranslation } from '@zurich/common/hooks';
import type { StepNames } from '~/lib/types';
import {
  autoPhotoUploadURLUniqueKeySelectorFamily,
  damagesFieldSelector,
  photosFieldSelector,
} from '~/state';
import { compressBlob } from '~/utils';
import { useGetPhotoBlob, usePostVSGAPhoto } from './api';
import { useDeleteVSGAPhoto } from './api/useDeleteVSGAPhoto';

/**
 * `usePhotoUpload` is used in combination with
 * components such as `CameraButton`to set the
 * base64 data and filename of an uploaded photo.
 *
 * @param id unique name for file without file type
 * @returns
 */
export function usePhotoUpload(id: string, stepName: StepNames) {
  const { t } = useTranslation();
  const { debug } = useBaseConfig();
  const { fetch: postPhoto, dataState: postDataStata } = usePostVSGAPhoto();
  const { fetch: deletePhoto, dataState: deleteDataState } =
    useDeleteVSGAPhoto();

  const [error, setError] = useState<string | undefined>(undefined);
  const [photo, setPhoto] = useRecoilState(
    autoPhotoUploadURLUniqueKeySelectorFamily({ id })
  );

  const { data, dataState } = useGetPhotoBlob(id);

  const setPhotos = useSetRecoilState(photosFieldSelector);
  const setDamages = useSetRecoilState(damagesFieldSelector);

  const handleImageBlob = async (file: File) => {
    if (error) {
      setError(undefined);
    }

    const formData = new FormData();
    try {
      const compressedBlob = await compressBlob(file, debug ? 1000000 : 3);
      formData.set('files', compressedBlob, file.name);

      const postResponse = await postPhoto({
        params: {
          body: formData,
        },
      });

      const documentId = postResponse.documentId;
      const fileId = postResponse.fileInfos?.at(0)?.id;

      setPhoto((prev) => ({
        ...prev,
        filename: `${id}.jpeg`,
        id,
        area: id,
        documentId,
        fileId,
      }));
    } catch (e: any) {
      setError(t('errors.generalUploadError'));
      console.error('Error in photo Upload', e);
    }
  };

  function resetImageBlob() {
    if (photo.documentId) {
      void deletePhoto({
        params: {
          vars: {
            documentId: photo.documentId,
          },
        },
      }).then(() => {
        setPhotos((prev) => prev.filter((photo) => photo.id !== id));
        setDamages((prev) => prev.filter((damage) => damage.area !== stepName));
      });
    }
  }

  const loading =
    postDataStata === 'loading' ||
    deleteDataState === 'loading' ||
    dataState === 'loading';

  return {
    blobURL: data ? URL.createObjectURL(data) : photo.imageURL,
    loading,
    error,
    handleImageBlob,
    resetImageBlob,
  };
}
